/**
 * 色板：从文档**自动提取颜色** → 映射到品牌色 → 一条命令**应用映射**。
 *
 * 与参考产品（svgeditai.com）对齐的三件事：
 *  1) 提取带**使用计数**（列表按「N 个元素」降序，跟截图一致）；
 *  2) 映射表是「源色 → 目标色」，应用是一次可撤销的批量改写；
 *  3) 「智能映射」按**角色**配对：中性色走品牌的中性轴（Ink↔Paper 明暗），
 *     彩色按使用频次吃品牌主色，再按色相就近微调。
 *
 * 「保留亮度关系」是应用阶段的开关（不参与选色）：把目标色的 HSL 亮度调成源色的亮度，
 * 这样原图里"深描边/浅底"的对比关系不会被品牌色抹平。
 */
import { hueDistance, isNeutral, lightness, normalizeColor, parseColor, relativeLuminance, toHex, withLightness, type Rgba } from './color';
import { pathOf, walk } from './dom';
import type { Command } from './history';
import { attrPatch, type Patch } from './patch';
import { readPresentation } from './query/props';
import { parseStyleAttr, styleValue } from './style-attr';

/** 参与取色的呈现属性（顺序固定 → 计数结果可复现） */
export const PAINT_ATTRS = ['fill', 'stroke', 'stop-color', 'flood-color', 'lighting-color', 'color'] as const;

export type PaintRole = 'fill' | 'stroke' | 'text' | 'stop' | 'other';

export interface ColorUsage {
  /** 归一化色值 `#rrggbb`（带透明度时 8 位） */
  hex: string;
  rgba: Rgba;
  /** 用到该色的元素（文档序；同元素多个属性同色只算一次） */
  elements: Element[];
  /** 用到该色的**元素数**（= elements.length；UI 列表直接读） */
  count: number;
  /** 出现次数（属性 + 内联 style 各算一次） */
  samples: number;
  /** 黑白灰 */
  neutral: boolean;
  /** 用途（用于"按角色"映射与列表标记） */
  roles: PaintRole[];
}

const roleOf = (tag: string, attr: string): PaintRole => {
  if (attr === 'fill') return tag === 'text' || tag === 'tspan' ? 'text' : 'fill';
  if (attr === 'stroke') return 'stroke';
  if (attr === 'stop-color') return 'stop';
  return 'other';
};

/**
 * 收集文档里用到的颜色（含 `defs` 里渐变的 stop）。
 * 计数只看**当前生效值**（内联 style 优先于呈现属性，与 query/props 一致）。
 */
export function collectColorUsage(root: Element): ColorUsage[] {
  const found = new Map<
    string,
    { rgba: Rgba; els: Set<Element>; samples: number; roles: Set<PaintRole> }
  >();

  walk(root, (el, path) => {
    if (path.length === 0) return; // 根自身不算
    const tag = el.tagName.toLowerCase();
    for (const attr of PAINT_ATTRS) {
      const value = readPresentation(el, attr);
      const hex = normalizeColor(value);
      if (!hex) continue; // none / url(#…) / currentColor 等一律跳过
      const rgba = parseColor(value) as Rgba;
      let entry = found.get(hex);
      if (!entry) {
        entry = { rgba, els: new Set(), samples: 0, roles: new Set() };
        found.set(hex, entry);
      }
      entry.els.add(el);
      entry.samples += 1;
      entry.roles.add(roleOf(tag, attr));
    }
  });

  const ROLE_ORDER: PaintRole[] = ['fill', 'text', 'stroke', 'stop', 'other'];
  return Array.from(found, ([hex, entry]) => ({
    hex,
    rgba: entry.rgba,
    elements: Array.from(entry.els),
    count: entry.els.size,
    samples: entry.samples,
    neutral: isNeutral(entry.rgba),
    roles: ROLE_ORDER.filter((role) => entry.roles.has(role)),
  })).sort((a, b) => b.count - a.count || b.samples - a.samples || a.hex.localeCompare(b.hex));
}

// ------------------------------------------------------------------ 品牌色

export interface BrandColor {
  id: string;
  /** 展示名，如 `Brand · Indigo` */
  name: string;
  hex: string;
}

export interface BrandPack {
  id: string;
  name: string;
  colors: BrandColor[];
}

/** 与参考产品截图一致的默认品牌包 */
export const DEFAULT_BRAND_PACK: BrandPack = {
  id: 'default',
  name: 'Default',
  colors: [
    { id: 'brand-indigo', name: 'Brand · Indigo', hex: '#6366f1' },
    { id: 'accent-clay', name: 'Accent · Clay', hex: '#c96442' },
    { id: 'ink', name: 'Ink', hex: '#1f1b16' },
    { id: 'paper', name: 'Paper', hex: '#fafff2' },
    { id: 'success', name: 'Success', hex: '#5ba8a6' },
  ],
};

export function makeBrandColor(name: string, hex: string): BrandColor {
  const clean = normalizeColor(hex) ?? '#000000';
  const slug = (name || 'color').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return { id: `${slug || 'color'}-${clean.slice(1, 7)}`, name: name.trim() || clean, hex: clean };
}

// ------------------------------------------------------------------ 映射

/** 源色 `#rrggbb` → 目标色 `#rrggbb` */
export type ColorMapping = Record<string, string>;

export interface MappingOpts {
  /** 保留亮度关系（应用阶段把目标亮度对齐源色） */
  keepLuminance?: boolean;
  /** 只改这些元素（"仅应用到选中元素"）；不给则整篇 */
  only?: ReadonlySet<Element>;
}

export interface SmartMappingOpts {
  /** 中性色（黑白灰）是否参与映射，默认参与（默认包里就有 Ink/Paper） */
  includeNeutral?: boolean;
}

/**
 * 智能映射 —— **按角色对应**：
 *  · 中性源色 → 品牌中性轴按亮度就近（最深的几档吃 Ink，最亮的吃 Paper）
 *  · 彩色源色 → 品牌彩色先按"占位"分配（用量最大的几个各拿一个不同的品牌色），
 *    剩下的按色相就近归并
 * 纯函数、结果稳定（同输入同输出），便于单测与"可预期"的用户体验。
 */
export function smartMapping(
  usages: ColorUsage[],
  brand: BrandColor[],
  opts: SmartMappingOpts = {},
): ColorMapping {
  const includeNeutral = opts.includeNeutral ?? true;
  const colors = brand
    .map((c) => ({ ...c, rgba: parseColor(c.hex) }))
    .filter((c): c is BrandColor & { rgba: Rgba } => c.rgba !== null);
  const neutrals = colors.filter((c) => isNeutral(c.rgba)).sort((a, b) => relativeLuminance(a.rgba) - relativeLuminance(b.rgba));
  const chroma = colors.filter((c) => !isNeutral(c.rgba));

  const mapping: ColorMapping = {};
  const neon = (targets: Array<BrandColor & { rgba: Rgba }>, source: Rgba): string | null => {
    if (targets.length === 0) return null;
    let best = targets[0] as BrandColor & { rgba: Rgba };
    let bestD = Number.POSITIVE_INFINITY;
    for (const t of targets) {
      const d = Math.abs(relativeLuminance(t.rgba) - relativeLuminance(source));
      if (d < bestD) {
        bestD = d;
        best = t;
      }
    }
    return best.hex;
  };

  if (includeNeutral && neutrals.length > 0) {
    for (const usage of usages.filter((u) => u.neutral)) {
      const hex = neon(neutrals, usage.rgba);
      if (hex) mapping[usage.hex] = hex;
    }
  }

  const chromaUsages = usages.filter((u) => !u.neutral);
  if (chroma.length > 0) {
    // ① 每个品牌彩色最多只领一个源色 —— 用**全局最小代价匹配**而不是"按用量贪心"。
    //    贪心的坑：用量大的青灰会先把 Success 占走，真正该配 Success 的青色只能去红色，
    //    结果是"配色整体错位"。这里用位掩码 DP 求全局最优（代价 = 色相距离 + 亮度差×60）。
    const cost = (target: Rgba, source: Rgba): number =>
      hueDistance(target, source) + Math.abs(relativeLuminance(target) - relativeLuminance(source)) * 60;

    const sources = chromaUsages.slice(0, 60); // 防御：源色极多时只对前 60 个做匹配（其余走②）
    const take = Math.min(chroma.length, 10); // 位掩码上限（2^10）
    const memo = new Map<number, { cost: number; assign: number[] }>();
    const solve = (i: number, mask: number): { cost: number; assign: number[] } => {
      if (i >= sources.length) return { cost: 0, assign: [] };
      const key = i * 2048 + mask;
      const cached = memo.get(key);
      if (cached) return cached;
      const source = (sources[i] as ColorUsage).rgba;
      let best = solve(i + 1, mask); // 本轮先不分配（留给②）
      best = { cost: best.cost, assign: [-1, ...best.assign] };
      for (let j = 0; j < take; j += 1) {
        if ((mask & (1 << j)) !== 0) continue;
        const target = chroma[j] as BrandColor & { rgba: Rgba };
        const rest = solve(i + 1, mask | (1 << j));
        const total = cost(target.rgba, source) + rest.cost;
        if (total < best.cost) best = { cost: total, assign: [j, ...rest.assign] };
      }
      memo.set(key, best);
      return best;
    };
    const plan = solve(0, 0).assign;
    const assigned = new Set<string>();
    plan.forEach((targetIdx, i) => {
      const usage = sources[i] as ColorUsage;
      if (targetIdx < 0) return;
      mapping[usage.hex] = (chroma[targetIdx] as BrandColor).hex;
      assigned.add(usage.hex);
    });

    // ② 剩下的（含没被匹配上的）按色相就近归并到全部品牌彩色
    for (const usage of chromaUsages) {
      if (assigned.has(usage.hex)) continue;
      let best = chroma[0] as BrandColor & { rgba: Rgba };
      let bestScore = Number.POSITIVE_INFINITY;
      for (const target of chroma) {
        const score = cost(target.rgba, usage.rgba);
        if (score < bestScore) {
          bestScore = score;
          best = target;
        }
      }
      mapping[usage.hex] = best.hex;
    }
  }

  return mapping;
}

/**
 * 「保留亮度关系」的靠拢比例：向源色亮度靠 65%，**品牌色自己留 35% 的亮度**。
 * 为什么不是 100%：全量对齐时"白底 → Indigo"会被抹成纯白、"黑字 → Paper"被抹成纯黑，
 * 品牌色直接消失、用户看着像"点了没反应"。留一点自身亮度才能既保住明暗对比、又看出是品牌色。
 */
const KEEP_LUMINANCE_BLEND = 0.65;

/** 单个源色 → 目标色的**最终落盘色**（含保留亮度调整）；null = 无效或映射到自身 */
export function resolveOne(
  source: { hex: string; rgba: Rgba },
  target: string,
  opts: MappingOpts = {},
): string | null {
  const parsed = parseColor(target);
  if (!parsed) return null;
  let finalHex = toHex(parsed, { alpha: false });
  if (opts.keepLuminance) {
    const sourceL = lightness(source.rgba);
    const targetL = lightness(parsed);
    finalHex = toHex(withLightness(parsed, targetL + (sourceL - targetL) * KEEP_LUMINANCE_BLEND), { alpha: false });
  }
  if (finalHex.toLowerCase() === source.hex.toLowerCase()) return null; // 映射到自身 → 不动
  return finalHex;
}

/** 把映射结果算成"最终落盘色值"（含保留亮度的调整） */
export function resolveMapping(
  usages: ColorUsage[],
  mapping: ColorMapping,
  opts: MappingOpts = {},
): Map<string, string> {
  const out = new Map<string, string>();
  for (const usage of usages) {
    const target = mapping[usage.hex];
    if (!target) continue;
    const finalHex = resolveOne(usage, target, opts);
    if (finalHex) out.set(usage.hex, finalHex);
  }
  return out;
}

/** 覆盖单个 style 声明（保留其它声明与顺序） */
const styleWithProp = (value: string | null | undefined, name: string, next: string): string | null => {
  const decls = parseStyleAttr(value);
  if (!decls.some(([prop]) => prop === name)) return null;
  return decls.map(([prop, val]) => `${prop}: ${prop === name ? next : val};`).join(' ');
};

/**
 * 生成「应用映射」命令：**一条命令、可 Ctrl+Z 撤销**。
 * 内联 style 里的声明原地改写（否则会被 style 盖住、改了没反应）。
 */
export function applyColorMapping(
  root: Element,
  usages: ColorUsage[],
  mapping: ColorMapping,
  opts: MappingOpts = {},
): Command {
  const targets = resolveMapping(usages, mapping, opts);
  const patches: Patch[] = [];
  if (targets.size === 0) return { label: '应用色板映射', patches };

  walk(root, (el, path) => {
    if (path.length === 0) return;
    if (opts.only && !opts.only.has(el)) return;
    const style = el.getAttribute('style');
    for (const attr of PAINT_ATTRS) {
      const styleHas = styleValue(style, attr);
      const current = readPresentation(el, attr);
      const hex = normalizeColor(current);
      if (!hex) continue;
      const next = targets.get(hex);
      if (!next) continue;
      if (styleHas !== null) {
        const restyled = styleWithProp(style, attr, next);
        if (restyled !== null) {
          const p = attrPatch(path, 'style', style, restyled);
          if (p) patches.push(p);
        }
        continue; // style 优先：改了 style 就够，别再写属性（避免两处不一致）
      }
      const p = attrPatch(path, attr, el.getAttribute(attr), next);
      if (p) patches.push(p);
    }
  });

  const label = `应用色板映射（${targets.size} 色 · ${patches.length} 处）`;
  return { label, patches };
}

/**
 * 把**指定元素**上写着的 `from` 色改成 `to`。
 *
 * 为什么需要它：颜色一旦被替换，文档里就再也找不到"源色"了 ——
 * 若第二次选色还按源色匹配，就会"点了没反应"（真踩过这类语义坑）。
 * 所以按**元素**定位，且 `from` 允许是「源色 / 上一次写入的颜色」两者之一。
 *
 * @returns 只有真改到了东西才返回带 patch 的命令（否则 patches 为空 → 不进历史）
 */
export function recolorElements(root: Element, elements: Element[], from: string[], to: string): Command {
  const want = new Set(from.map((h) => h.toLowerCase()));
  const patches: Patch[] = [];

  for (const el of elements) {
    const path = pathOf(el, root);
    if (!path || path.length === 0) continue; // 根元素本身不参与
    const style = el.getAttribute('style');
    for (const attr of PAINT_ATTRS) {
      const hex = normalizeColor(readPresentation(el, attr));
      if (!hex || !want.has(hex.toLowerCase())) continue;
      if (styleValue(style, attr) !== null) {
        const restyled = styleWithProp(style, attr, to);
        if (restyled !== null) {
          const p = attrPatch(path, 'style', style, restyled);
          if (p) patches.push(p);
        }
        continue; // style 优先：改了 style 就够，别再写属性
      }
      const p = attrPatch(path, attr, el.getAttribute(attr), to);
      if (p) patches.push(p);
    }
  }

  return { label: `改色 → ${to}`, patches };
}

/** 一条"原字面量"记录：命中颜色的那个属性（或内联 style 整串）改写前的原样值 */
export interface PaintOrigin {
  el: Element;
  /** 呈现属性名；改的是内联 style 时固定为 `style`（整串原样，还原时写法不变） */
  attr: string;
  value: string | null;
}

/**
 * 记录"即将被改写的原字面量"，供之后**原样还原**用。
 *
 * 为什么要记原样而不是记色值：文档里可能写 `#FFF` / `white` / `fill:#fff;stroke:#000`，
 * 只按归一化色值回写会把用户的写法改掉（`#FFF` → `#ffffff`、style 被重排）。
 */
export function paintOrigins(root: Element, elements: Element[], hex: string): PaintOrigin[] {
  const want = hex.toLowerCase();
  const out: PaintOrigin[] = [];
  for (const el of elements) {
    if (!pathOf(el, root)?.length) continue;
    const style = el.getAttribute('style');
    for (const attr of PAINT_ATTRS) {
      if (normalizeColor(readPresentation(el, attr))?.toLowerCase() !== want) continue;
      if (styleValue(style, attr) !== null) {
        out.push({ el, attr: 'style', value: style });
        continue; // style 优先：改的就是整串 style
      }
      out.push({ el, attr, value: el.getAttribute(attr) });
    }
  }
  return out;
}

/* ------------------------------------------------------------------ 高亮
 * 「点某个颜色 → 它的元素被框选、其余压暗」是**纯视图态**：
 * 只往非命中元素写 `data-editor-dim`（导出时 data-editor-* 一律被剥），
 * 压暗用 `filter: opacity()` 而**不是** `opacity` —— filter 是在元素自身渲染结果上**乘**，
 * 所以元素原本的 opacity/半透明不会被覆盖。
 */
export const DIM_ATTR = 'data-editor-dim';

/** 非图形元素不参与（对它们压暗没有意义，还会污染输出） */
const NON_GRAPHIC_DIM = new Set(['title', 'desc', 'style', 'metadata', 'defs']);

/** 清掉全部压暗标记 */
export function clearSpotlight(root: Element): void {
  for (const el of Array.from(root.querySelectorAll(`[${DIM_ATTR}]`))) el.removeAttribute(DIM_ATTR);
}

/**
 * 高亮某颜色的元素、压暗其余。
 *
 * 遍历规则（避免"一层叠一层"地把元素压到全黑）：
 *  · 命中元素：整棵子树保持原样（不再往里压暗）
 *  · 命中元素的祖先：不能压暗（否则会连带压暗里面的命中元素），继续往下走
 *  · 其它子树：**只压最外层**，不再逐层标记（否则 0.16² 几乎看不见）
 *
 * @returns 被压暗的元素数（0 表示文档里没有该颜色）
 */
export function markSpotlight(root: Element, hex: string | null): number {
  clearSpotlight(root);
  if (!hex) return 0;

  const usage = collectColorUsage(root).find((u) => u.hex === hex);
  if (!usage || usage.elements.length === 0) return 0;

  const match = new Set(usage.elements);
  const keep = new Set<Element>();
  for (const el of match) {
    for (let cur: Element | null = el; cur && cur !== root; cur = cur.parentElement) keep.add(cur);
  }

  let marked = 0;
  const sweep = (parent: Element): void => {
    for (const child of Array.from(parent.children)) {
      if (NON_GRAPHIC_DIM.has(child.tagName.toLowerCase())) continue;
      if (match.has(child)) continue; // 命中：整棵子树原样
      if (keep.has(child)) {
        sweep(child); // 命中元素的祖先：继续往下找
        continue;
      }
      child.setAttribute(DIM_ATTR, '1');
      marked += 1;
    }
  };
  sweep(root);
  return marked;
}

/** 列表里每个源色的目标色（UI 直接读） */
export function targetOf(mapping: ColorMapping, hex: string): string | null {
  return mapping[hex] ?? null;
}

/** 该颜色是否只用于描边（列表标记用） */
export const isStrokeOnly = (usage: ColorUsage): boolean =>
  usage.roles.length === 1 && usage.roles[0] === 'stroke';

/** 色彩角色摘要（列表第二行的补充信息） */
export function roleLabel(usage: ColorUsage): string {
  const names: Record<PaintRole, string> = {
    fill: '填充',
    text: '文字',
    stroke: '描边',
    stop: '渐变',
    other: '其它',
  };
  return usage.roles.map((role) => names[role]).join('/');
}
